import { useCallback, useEffect, useState } from "react";
import { ExternalLink, Check, X, MessageCircle } from "lucide-react";
import { toast } from "sonner";
import { api, customerWaLink } from "@/lib/api";
import { photoSrc } from "@/components/Reviews";
import MonthlyReports from "@/components/admin/MonthlyReports";

const inr = (n) => `₹${Number(n || 0).toLocaleString("en-IN")}`;

export default function AgentsAdmin() {
  const [agents, setAgents] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [refs, setRefs] = useState({});
  const [notify, setNotify] = useState(null);
  const load = useCallback(async () => {
    const [a, w] = await Promise.all([api.get("/admin/agents"), api.get("/admin/withdrawals")]);
    setAgents(a.data); setWithdrawals(w.data);
  }, []);
  useEffect(() => { load().catch(() => toast.error("Could not load agents")); }, [load]);

  const first = (n) => (n || "there").split(" ")[0];
  const kyc = async (uid, status) => {
    const note = status === "rejected" ? window.prompt("Reason shown to the agent (optional)") || "" : "";
    await api.patch(`/admin/agents/${uid}/kyc`, { status, note });
    const a = agents.find((x) => x.user_id === uid);
    toast.success(`KYC ${status}`); load();
    if (a?.phone) setNotify({ kind: "kyc", name: a.name, phone: a.phone, msg: status === "approved"
      ? `Hi ${first(a.name)}! Your Random Rider agent KYC is approved ✅ You can now withdraw your referral earnings from randomrider.com/account?tab=earn — Team Random Rider`
      : `Hi ${first(a.name)}, we couldn't verify your KYC${note ? ` (${note})` : ""}. Please resubmit at randomrider.com/account?tab=earn — Team Random Rider` });
  };
  const payout = async (id, status) => {
    const w = withdrawals.find((x) => x.id === id);
    const a = agents.find((x) => x.user_id === w?.agent_user_id);
    const ref = refs[id] || "";
    await api.patch(`/admin/withdrawals/${id}`, { status, reference: ref });
    toast.success(status === "paid" ? "Marked paid" : "Rejected — amount returned to wallet"); load();
    if (a?.phone && w) setNotify({ kind: "payout", name: a.name, phone: a.phone, msg: status === "paid"
      ? `Hi ${first(a.name)}! Your Random Rider payout of ₹${Number(w.amount).toLocaleString("en-IN")} has been transferred${ref ? ` (ref ${ref})` : ""}. Thank you for sharing Puri with your friends! — Team Random Rider`
      : `Hi ${first(a.name)}, your withdrawal of ₹${Number(w.amount).toLocaleString("en-IN")} couldn't be processed and has been returned to your wallet. Please check your bank details at randomrider.com/account?tab=earn — Team Random Rider` });
  };

  const open = withdrawals.filter((w) => w.status === "requested");
  const pendingKyc = agents.filter((a) => a.kyc?.status === "pending");

  return (
    <div className="space-y-8" data-testid="agents-admin">
      {notify && (
        <div className="bg-ink text-sand rounded-2xl px-5 py-4 flex flex-wrap items-center justify-between gap-3" data-testid="agent-notify-bar">
          <p className="text-sm">Tell <span className="font-semibold">{notify.name}</span> on WhatsApp about the {notify.kind === "kyc" ? "KYC decision" : "payout"}.</p>
          <div className="flex gap-2">
            <a href={customerWaLink(notify.phone, notify.msg)} target="_blank" rel="noreferrer" onClick={() => setNotify(null)} data-testid="agent-notify-whatsapp"
              className="inline-flex items-center gap-1.5 bg-[#25D366] text-white text-xs font-semibold px-4 py-2 rounded-full hover:brightness-110"><MessageCircle size={13} /> Open WhatsApp</a>
            <button onClick={() => setNotify(null)} data-testid="agent-notify-dismiss" className="text-xs text-sand/60 px-2">Later</button>
          </div>
        </div>
      )}
      <section>
        <h2 className="font-serif text-2xl font-bold text-ink">Payout requests <span className="text-ink/40 text-base font-normal">({open.length} open)</span></h2>
        <div className="grid gap-3 mt-4">
          {open.map((w) => {
            const a = agents.find((x) => x.user_id === w.agent_user_id);
            return (
              <div key={w.id} className="bg-white border border-ink/10 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`withdrawal-row-${w.id.slice(0, 8)}`}>
                <div>
                  <p className="font-semibold text-ink">{w.agent_name} <span className="text-ink/40 font-normal">· {w.agent_email}</span></p>
                  {a?.kyc && <p className="text-xs text-ink/60 mt-1">{a.kyc.full_name} · {a.kyc.bank_name} · A/c {a.kyc.account_number} · {a.kyc.ifsc}{a.kyc.upi_id && ` · UPI ${a.kyc.upi_id}`}</p>}
                  <p className="text-[11px] text-ink/40 font-mono mt-1">{new Date(w.created_at).toLocaleString("en-IN")}</p>
                </div>
                <div className="flex items-center gap-2">
                  <span className="font-serif text-xl font-bold text-ink">{inr(w.amount)}</span>
                  <input placeholder="UTR / ref" value={refs[w.id] || ""} onChange={(e) => setRefs({ ...refs, [w.id]: e.target.value })} className="w-32 bg-sand border border-ink/15 rounded-full px-3 py-1.5 text-xs focus:outline-none" data-testid={`withdrawal-ref-${w.id.slice(0, 8)}`} />
                  <button onClick={() => payout(w.id, "paid")} data-testid={`withdrawal-paid-${w.id.slice(0, 8)}`} className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full"><Check size={12} /> Paid</button>
                  <button onClick={() => payout(w.id, "rejected")} data-testid={`withdrawal-reject-${w.id.slice(0, 8)}`} className="flex items-center gap-1 border border-ink/15 text-ink/60 text-xs font-semibold px-3 py-1.5 rounded-full hover:text-red-600"><X size={12} /> Reject</button>
                </div>
              </div>
            );
          })}
          {open.length === 0 && <p className="text-sm text-ink/50" data-testid="withdrawals-empty">No open payout requests.</p>}
        </div>
      </section>

      {withdrawals.some((w) => w.status !== "requested") && (
        <section>
          <h3 className="font-serif text-lg font-bold text-ink/70">Recent payouts</h3>
          <ul className="mt-2 divide-y divide-ink/10 bg-white border border-ink/10 rounded-2xl px-4 text-sm" data-testid="payout-history">
            {withdrawals.filter((w) => w.status !== "requested").slice(0, 8).map((w) => {
              const a = agents.find((x) => x.user_id === w.agent_user_id);
              return (
                <li key={w.id} className="py-2.5 flex items-center justify-between gap-3" data-testid={`payout-history-${w.id.slice(0, 8)}`}>
                  <span className="text-ink/70 truncate">{w.agent_name} · {new Date(w.updated_at).toLocaleDateString("en-IN")}{w.reference && ` · ref ${w.reference}`}</span>
                  <span className="flex items-center gap-2"><span className={`text-xs font-semibold ${w.status === "paid" ? "text-emerald-700" : "text-ink/40"}`}>{w.status}</span><span className="font-semibold">{inr(w.amount)}</span>
                    {a?.phone && w.status === "paid" && <a href={customerWaLink(a.phone, `Hi ${first(a.name)}! Your Random Rider payout of ${inr(w.amount)} has been transferred${w.reference ? ` (ref ${w.reference})` : ""}. Thank you for sharing Puri! — Team Random Rider`)} target="_blank" rel="noreferrer" aria-label="WhatsApp" data-testid={`payout-whatsapp-${w.id.slice(0, 8)}`} className="p-1.5 rounded-full text-[#25D366] hover:bg-sand"><MessageCircle size={14} /></a>}
                  </span>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <MonthlyReports />

      <section>
        <h2 className="font-serif text-2xl font-bold text-ink">KYC approvals <span className="text-ink/40 text-base font-normal">({pendingKyc.length} pending)</span></h2>
        <div className="grid gap-3 mt-4">
          {pendingKyc.map((a) => (
            <div key={a.user_id} className="bg-white border border-ink/10 rounded-2xl p-5 flex flex-wrap items-center justify-between gap-3" data-testid={`kyc-row-${a.user_id.slice(5, 13)}`}>
              <div>
                <p className="font-semibold text-ink">{a.kyc.full_name} <span className="text-ink/40 font-normal">· {a.email}</span></p>
                <p className="text-xs text-ink/60 mt-1">{a.kyc.bank_name} · A/c {a.kyc.account_number} · {a.kyc.ifsc}{a.kyc.upi_id && ` · UPI ${a.kyc.upi_id}`}</p>
                {a.kyc.id_doc_url && <a href={photoSrc(a.kyc.id_doc_url)} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs text-terra font-semibold mt-1" data-testid={`kyc-doc-${a.user_id.slice(5, 13)}`}>View ID document <ExternalLink size={11} /></a>}
              </div>
              <div className="flex gap-2">
                <button onClick={() => kyc(a.user_id, "approved")} data-testid={`kyc-approve-${a.user_id.slice(5, 13)}`} className="flex items-center gap-1 bg-emerald-600 text-white text-xs font-semibold px-3 py-1.5 rounded-full"><Check size={12} /> Approve</button>
                <button onClick={() => kyc(a.user_id, "rejected")} data-testid={`kyc-reject-${a.user_id.slice(5, 13)}`} className="flex items-center gap-1 border border-ink/15 text-ink/60 text-xs font-semibold px-3 py-1.5 rounded-full hover:text-red-600"><X size={12} /> Reject</button>
              </div>
            </div>
          ))}
          {pendingKyc.length === 0 && <p className="text-sm text-ink/50" data-testid="kyc-empty">No KYC submissions waiting.</p>}
        </div>
      </section>

      <section>
        <h2 className="font-serif text-2xl font-bold text-ink">Agents & customers <span className="text-ink/40 text-base font-normal">({agents.length})</span></h2>
        <div className="overflow-x-auto mt-4 bg-white border border-ink/10 rounded-2xl">
          <table className="w-full text-sm" data-testid="agents-table">
            <thead className="text-[11px] uppercase tracking-wider text-ink/50 text-left"><tr><th className="px-4 py-3">Customer</th><th className="px-4 py-3">Code</th><th className="px-4 py-3">Wallet</th><th className="px-4 py-3">KYC</th><th className="px-4 py-3">Joined</th><th className="px-4 py-3"></th></tr></thead>
            <tbody className="divide-y divide-ink/10">
              {agents.map((a) => (
                <tr key={a.user_id} data-testid={`agent-row-${a.user_id.slice(5, 13)}`}>
                  <td className="px-4 py-3"><p className="font-semibold text-ink">{a.name}</p><p className="text-xs text-ink/50">{a.email}</p></td>
                  <td className="px-4 py-3 font-mono text-xs">{a.referral_code}</td>
                  <td className="px-4 py-3 font-semibold">{inr(a.wallet_balance)}</td>
                  <td className="px-4 py-3 capitalize text-xs">{a.kyc?.status || "none"}</td>
                  <td className="px-4 py-3 text-xs text-ink/50">{new Date(a.created_at).toLocaleDateString("en-IN")}</td>
                  <td className="px-4 py-3">{a.phone && <a href={customerWaLink(a.phone, `Hi ${first(a.name)}, this is Random Rider about your agent account.`)} target="_blank" rel="noreferrer" aria-label="WhatsApp agent" data-testid={`agent-whatsapp-${a.user_id.slice(5, 13)}`} className="inline-flex p-2 rounded-full text-[#25D366] hover:bg-sand"><MessageCircle size={15} /></a>}</td>
                </tr>
              ))}
              {agents.length === 0 && <tr><td colSpan={6} className="px-4 py-6 text-center text-ink/50">No customer accounts yet.</td></tr>}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
